// What screens and the (lazy-loaded) world stage share. Screens write what their set should
// show and how to react; the stage reports whether it's live and the current sky colours.
// Kept tiny and three-free so it's in the main bundle.
import { create } from 'zustand'
import type { Figure } from '../../shared/family'
import type { FlagData, PinData } from '../map/world/markers'
import type { ActInfo, MapEvents, PlaceInfo } from './sets/mapSet'
import { placeId, placeOrder, PLACES } from '../map/places/registry'
import type { TripSeason } from '../map/seasons'
import type { ArcadeLook } from './sets/arcadeSet'
import type { CrateLook } from './sets/crateSet'
import type { LearnLook } from './sets/learnSet'
import type { TripLook } from './sets/tripSet'
import type { CrateToy } from '../domain/crateToys'

export type StageStatus = 'off' | 'loading' | 'live' | 'failed'

const noop = () => {}

const SPOTTED_KEY = 'tq-spotted'
/** WI-13, a world per child: each player's Spotted is their own (`tq-spotted:<person>`). */
let spottedKey = SPOTTED_KEY

// Per-phone collection (a convenience; storage may be unavailable, so never required).
function readSpotted(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(spottedKey) ?? '{}') as Record<string, string>
  } catch {
    return {}
  }
}

/** Whose Spotted the Map fills and shows (the signed-in person; null: the phone's shared one). */
export function setSpottedBy(personId: string | null) {
  const key = personId ? `${SPOTTED_KEY}:${personId}` : SPOTTED_KEY
  if (key === spottedKey) return
  spottedKey = key
  useStage.getState().set({ spotted: readSpotted() })
}

export function markSpotted(id: string) {
  const s = useStage.getState()
  if (s.spotted[id]) return
  const spotted = { ...s.spotted, [id]: new Date().toISOString() }
  s.set({ spotted })
  try {
    localStorage.setItem(spottedKey, JSON.stringify(spotted))
  } catch {
    // ignore
  }
}

const EXPLORE_KEY = 'tq-explore'

/** A grown-up's Explore all, kept on this phone (WI-07); a child's Map ignores it (MapScreen). */
function storedExplore(): boolean {
  try {
    return localStorage.getItem(EXPLORE_KEY) === '1'
  } catch {
    return false
  }
}

export function setExplore(on: boolean) {
  useStage.getState().set({ explore: on })
  try {
    if (on) localStorage.setItem(EXPLORE_KEY, '1')
    else localStorage.removeItem(EXPLORE_KEY)
  } catch {
    // ignore
  }
}

const ISLAND_KEY = 'tq-island'

/** The island remembered on this phone (an old place id reads as today's), or null. */
function storedIsland(): string | null {
  try {
    const id = localStorage.getItem(ISLAND_KEY)
    return id ? placeId(id) : null
  } catch {
    return null
  }
}

/**
 * The Map island last shown on this phone (a convenience), else the first of the trip's places
 * (`trip`: tripPlaces of the active trip), else the first place.
 */
export function readIsland(trip: readonly string[] = []): string {
  return storedIsland() ?? trip[0] ?? PLACES[0]!.id
}

/** Remembers a trip place (other places need Explore all, which is per session, so they aren't). */
export function rememberIsland(id: string) {
  if (!useStage.getState().tripPlaces.includes(placeId(id))) return
  try {
    localStorage.setItem(ISLAND_KEY, placeId(id))
  } catch {
    // ignore
  }
}

/**
 * The active trip's places (map/places/registry tripPlaces: its stages' spots, in stage order).
 * Before the Map is up it opens on `start` (map/bindings islandNow: the stage the family is in now,
 * or the first before the trip; WI-04), else on the island remembered, else the first trip place.
 */
export function setTripPlaces(ids: readonly string[], start?: string) {
  const s = useStage.getState()
  const open = s.status === 'off' ? (start ?? storedIsland() ?? ids[0]) : undefined
  const same = ids.join(',') === s.tripPlaces.join(',')
  if (same && (!open || open === s.island)) return
  s.set({ ...(same ? {} : { tripPlaces: [...ids] }), ...(open ? { island: open } : {}) })
}

/** The trip's dates per place (map/seasons tripSeasons), for the places' seasons. */
export function setTripSeasons(rec: Record<string, TripSeason>) {
  const s = useStage.getState()
  if (JSON.stringify(rec) !== JSON.stringify(s.tripSeasons)) s.set({ tripSeasons: rec })
}

/** Every place (B4: any built place can be a trip island), in registry order, for titles. */
export const ALL_ISLANDS: readonly { id: string; title: string }[] = PLACES.map((p) => ({ id: p.id, title: p.title }))

/**
 * The island switcher's order: the trip's places first, in stage order, then the rest. Only those
 * with a stage the viewer can see are offered (map/bindings `shownIslands`) unless World →
 * Explore all is on, so a stage whose spot is `kyoto` brings Kyoto.
 */
export function islandOrder(trip: readonly string[]): { id: string; title: string }[] {
  return placeOrder(trip).map((p) => ({ id: p.id, title: p.title }))
}

/**
 * Shows a journey stop (the stop bar, the top pill's "back", a stop tapped on the Trip tab): the
 * Map flies to its island (`spot`; none for a wrapped surprise, whose island stays put). The request
 * goes even when the island is already showing, so it cancels a fly still loading.
 */
export function goStop(stop: number, spot: string | null) {
  const s = useStage.getState()
  s.set({ stop, ...(spot ? { goIsland: { id: spot, n: (s.goIsland?.n ?? 0) + 1 } } : {}) })
}

interface Bridge {
  status: StageStatus
  failReason: string | null
  sky: [string, string]
  /** Map pins/flags and the HUD bands (viewport px) labels hide behind. */
  pins: PinData[]
  flags: FlagData[]
  hud: { top: number; bottom: number }
  /** Bump to ease the Map camera back home. */
  home: number
  /** The island has been dragged at least once (hides the "drag to spin" hint). */
  interacted: boolean
  /** A sheet or the World panel is open over the Map: the island's idle turn waits. */
  sheet: boolean
  map: MapEvents
  /** The Map's act playing now (the "On the island" ticker), and every act (Surprise buttons). */
  act: ActInfo | null
  acts: { id: string; label: string; rarity: 'common' | 'rare' | 'legendary' }[]
  /** Rare acts seen on this phone: id → first seen (ISO). */
  spotted: Record<string, string>
  /** Bump to start an act now (Surprise buttons; an act key's "Go and see", which may name an act on the island being flown to). */
  forceAct: { id: string; n: number } | null
  /**
   * WI-13, a world per child: acts still locked for the player on each trip island (place → act ids).
   * The director never picks them and a tap doesn't start them; a grown-up's World panel still can.
   */
  lockedActs: Record<string, string[]>
  /** Bump to ease the Map camera to the act playing (ticker tap). */
  focusAct: number
  /**
   * The active trip's places (setTripPlaces): they follow the trip's live weather, come first in
   * the switcher and are remembered as the last island shown.
   */
  tripPlaces: string[]
  /** The trip's dates per place (map/seasons tripSeasons): a trip place's season follows them. */
  tripSeasons: Record<string, TripSeason>
  /** The island the Map shows, and a request to fly to another one. */
  island: string
  goIsland: { id: string; n: number } | null
  /**
   * The journey stop the Map is showing (map/journey: the trip's stages in order, repeats
   * included), or null until the Map picks one (the stop we're at) or on an island off the trip.
   */
  stop: number | null
  /** Islands this viewer may see (null: all); the Map set refuses to fly anywhere else. */
  islandsShown: string[] | null
  /** The signed-in child's figure, standing at "you are here" on the Map (null for grown-ups). */
  hero: Figure | null
  /** World → Explore all (per session, off by default): every island and place in the switcher. */
  explore: boolean
  /** The place showing (its World-panel options), or null while none is built yet. */
  place: PlaceInfo | null
  /**
   * World → Weather / Season on a place (ids; null = the place's first weather; for the season,
   * the trip's (tripSeasons), else the place's first). The season pick lasts the session.
   */
  placeWeather: string | null
  placeSeason: string | null
  /** The featured gashapon and where it sits on screen. */
  arcade: ArcadeLook
  /** Bump to twist the 3D machine (the HTML button). */
  twist: number
  arcadeEvents: { onDropped(): void; onBusy(busy: boolean): void }
  learn: LearnLook
  crate: CrateLook
  crateEvents: { onHint(toy: CrateToy): void; onBook?(id: string): void }
  trip: TripLook
  tripEvents: { onPick(stageId: string): void }
  /** §8.3: the journey act playing now, by name, for screen readers ("Toot!"); null when none. */
  tripAct: string | null
  set(patch: Partial<Omit<Bridge, 'set'>>): void
}

export const useStage = create<Bridge>((set) => ({
  status: 'off',
  failReason: null,
  sky: ['#1F63C4', '#8DBDEB'],
  pins: [],
  flags: [],
  hud: { top: 0, bottom: Infinity },
  home: 0,
  interacted: false,
  sheet: false,
  map: { onPin: noop, onFlag: noop, onEmpty: noop, onSfx: noop, onInteract: noop, onAct: noop, onSpotted: noop, onIsland: noop },
  act: null,
  acts: [],
  spotted: readSpotted(),
  forceAct: null,
  lockedActs: {},
  focusAct: 0,
  tripPlaces: [],
  tripSeasons: {},
  island: readIsland(),
  goIsland: null,
  stop: null,
  islandsShown: null,
  hero: null,
  explore: storedExplore(),
  place: null,
  placeWeather: null,
  placeSeason: null,
  arcade: { machine: 'gashapon', title: '', colour: 'red', locked: false, frame: { top: 0, bottom: 0 } },
  twist: 0,
  arcadeEvents: { onDropped: noop, onBusy: noop },
  learn: { frame: { top: 0, bottom: 0 }, board: '', card: '', blocks: [], figure: null },
  crate: { frame: { top: 0, bottom: 0 }, toys: [], lit: null },
  crateEvents: { onHint: noop },
  trip: { anchors: [], scroll: 0, arrival: false, focus: null },
  tripEvents: { onPick: noop },
  tripAct: null,
  set: (patch) => set(patch),
}))
